/**
 * Re-entry point for a template prompt the user put off with "Maybe later":
 * the leading tile in the home browser's Canvases tab. Clicking it reopens the
 * dialog, which owns the preview, the copy and every error state — this stays a
 * pure re-entry point. Clearing it is terminal (PATCH `template_prompt: null`).
 */

import { cn } from "@/utils";
import { WorkspaceGridCard } from "@cohesive/ui-components";
import { Compass, X } from "lucide-react";
import { useState } from "react";
import { useTemplateCopyPromptContext } from "./TemplateCopyPromptContext";
import { buildTemplateOgImageUrl } from "./publicTemplateUrls";

/** Rust accent from the onboarding `_co_onb` palette (`--co-rust`). */
const RUST = "#B86E49";

interface TemplateCopyPromptCardProps {
  /**
   * `"card"` (default) is the grid tile. `"row"` renders a matching list row
   * for the home browser's list view.
   */
  variant?: "card" | "row";
}

export function TemplateCopyPromptCard({
  variant = "card",
}: TemplateCopyPromptCardProps) {
  const context = useTemplateCopyPromptContext();
  const [imageFailed, setImageFailed] = useState(false);

  const prompt = context?.prompt ?? null;
  if (!context || !prompt || context.view !== "card") return null;

  const actionLabel =
    prompt.kind === "file"
      ? "View this file"
      : prompt.authorName
        ? `Copy ${prompt.authorName}'s template`
        : "Copy this template";

  const showOgImage = prompt.kind === "canvas" && !imageFailed;

  if (variant === "row") {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={actionLabel}
        data-testid="template-copy-prompt-card"
        onClick={context.openDialog}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            context.openDialog();
          }
        }}
        className={cn(
          "group flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-md px-2.5 py-1 text-left outline-none transition-colors",
          "hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#B86E49]/25 bg-gradient-to-br from-[#F5F0E5] to-[#EBE3D1] dark:from-[#221f19] dark:to-[#19160f]">
          <Compass className="size-4 text-[#B86E49]" />
        </span>

        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">
            {prompt.title || "Your template"}
          </span>
        </span>

        {/* Empty updated/owner columns keep the dismiss aligned with the
         *  rows' kebab lane. */}
        <span aria-hidden className="hidden w-24 shrink-0 @sm:block" />
        <span aria-hidden className="hidden w-32 shrink-0 @md:block" />

        <span
          className="flex w-7 shrink-0 justify-end"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Dismiss template prompt"
            data-testid="template-copy-prompt-card-clear"
            onClick={context.resolve}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-n-10 hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </span>
      </div>
    );
  }

  const preview = (
    <>
      <button
        type="button"
        aria-label="Don't show again"
        title="Don't show again"
        data-testid="template-copy-prompt-card-clear"
        onClick={(e) => {
          e.stopPropagation();
          context.resolve();
        }}
        className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-background/70 text-muted-foreground backdrop-blur transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {showOgImage ? (
        <img
          src={buildTemplateOgImageUrl(prompt.id)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#F5F0E5] to-[#EBE3D1] dark:from-[#221f19] dark:to-[#19160f]">
          <div
            aria-hidden
            className="pointer-events-none absolute h-24 w-24 rounded-full blur-2xl"
            style={{ background: `${RUST}3D` }}
          />
          <Compass
            className="relative h-10 w-10 transition-transform duration-200 group-hover:scale-105"
            style={{ color: RUST }}
            strokeWidth={1.75}
          />
        </div>
      )}
    </>
  );

  // Footer markup is local: the grid card's footer helpers aren't exported
  // from ui-components.
  const footer = (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-foreground">
        {prompt.title || "Your template"}
      </p>
      <p className="truncate text-xs leading-snug text-muted-foreground">
        {actionLabel}
      </p>
    </div>
  );

  return (
    <WorkspaceGridCard
      aria-label={actionLabel}
      data-testid="template-copy-prompt-card"
      {...({ alt: "template-copy-prompt-card" } as Record<string, string>)}
      onClick={context.openDialog}
      preview={preview}
      footer={footer}
      interactive
      previewClassName="border-[#B86E49]/25 group-hover:border-[#B86E49]/70"
    />
  );
}
